import cmApi from "@/api/controllers/content-monitoring";
import {
  CM_SET_FORMAT,
  CM_SET_WORDS,
  CM_LOAD_WORDS,
  CM_ADD_WORD,
  CM_REMOVE_WORD,
  CM_SET_OWNER,
  CM_SET_CATEGORY,
  CM_SET_SOCIALS,
} from "../actions/contentMonitoring";

/**
 * Фильтры вкладки Content Monitoring.
 *
 * Живут в сторе, потому что UI фильтров («Формат» и «Слова») рендерится в шапке
 * (.dash-options), а данные по ним грузит тело вкладки — это разные компоненты.
 *
 * Списки слов хранятся НА СЕРВЕРЕ (воркспейс CM), а не на клиенте: там же
 * происходит лемматизация свободного ввода, поэтому сохранённое слово может
 * отличаться от введённого («компаниях» → «компания»). Стор держит их копию.
 *
 * Известное ограничение: воркспейс в CM сейчас один на всех (workspace_id по
 * умолчанию), т.е. списки слов общие, а не персональные для пользователя Lucmus.
 */

const state = {
  format: "text", // text | media | all
  added: [], // ★ отслеживаемые
  excluded: [], // 🚫 исключённые (приоритетнее added)
  wordsLoaded: false,
  wordsError: "",
  // Скоуп по страницам: null — «все страницы» (без ограничения).
  // Владелец идентифицируется email аккаунта (page.email), категория — её id.
  owner: null,
  category: null,
  socials: [],
};

const getters = {
  getCmFormat: (s) => s.format,
  getCmAdded: (s) => s.added,
  getCmExcluded: (s) => s.excluded,
  getCmWordsError: (s) => s.wordsError,
  getCmOwner: (s) => s.owner,
  getCmCategory: (s) => s.category,
  getCmSocials: (s) => s.socials,
  /** Слово в обоих списках считается исключённым — то же правило, что в CM API. */
  getCmEffectiveAdded: (s) => s.added.filter((w) => !s.excluded.includes(w)),
};

const mutations = {
  [CM_SET_FORMAT](s, format) {
    s.format = format;
  },
  [CM_SET_OWNER](s, owner) {
    s.owner = owner || null;
  },
  [CM_SET_CATEGORY](s, category) {
    s.category = category != null ? category : null;
  },
  [CM_SET_SOCIALS](s, socials) {
    s.socials = Array.isArray(socials) ? socials.filter(Boolean) : [];
  },
  [CM_SET_WORDS](s, { added, excluded }) {
    s.added = added || [];
    s.excluded = excluded || [];
    s.wordsLoaded = true;
    s.wordsError = "";
  },
  CM_SET_WORDS_ERROR(s, message) {
    s.wordsError = message;
  },
};

const actions = {
  [CM_LOAD_WORDS]({ commit, state: s }) {
    if (s.wordsLoaded) return Promise.resolve();

    return cmApi
      .fetchWordLists()
      .then((lists) => commit(CM_SET_WORDS, lists))
      .catch((e) => commit("CM_SET_WORDS_ERROR", e.message));
  },

  /** normalize=true для свободного ввода: сервер вернёт лемму, её и показываем. */
  [CM_ADD_WORD]({ commit }, { list, word, normalize = false }) {
    const value = (word || "").trim();
    if (!value) return Promise.resolve();

    return cmApi
      .addWord(list, value, { normalize })
      .then((lists) => {
        commit(CM_SET_WORDS, lists);
        return lists.word; // как слово реально сохранилось
      })
      .catch((e) => {
        commit("CM_SET_WORDS_ERROR", e.message);
        throw e;
      });
  },

  [CM_REMOVE_WORD]({ commit }, { list, word }) {
    return cmApi
      .removeWord(list, word)
      .then((lists) => commit(CM_SET_WORDS, lists))
      .catch((e) => commit("CM_SET_WORDS_ERROR", e.message));
  },
};

export default { state, getters, mutations, actions };
